import { useProject } from '@/contexts/ProjectContext';

interface ProjectBadgeProps {
  projectId: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showName?: boolean;
  className?: string;
}

const sizeClasses = {
  xs: 'h-3 w-3',
  sm: 'h-5 w-5',
  md: 'h-8 w-8',
  lg: 'h-10 w-10',
};

const textSizeClasses = {
  xs: 'text-[8px]',
  sm: 'text-xs',
  md: 'text-sm',
  lg: 'text-base',
};

export function ProjectBadge({ 
  projectId, 
  size = 'sm', 
  showName = false,
  className = '' 
}: ProjectBadgeProps) {
  const { getProjectById, getProjectColor } = useProject();
  const project = getProjectById(projectId);
  
  if (!project) return null;

  const color = getProjectColor(projectId);
  const sizeClass = sizeClasses[size];
  const textSize = textSizeClasses[size];

  // Only show logo if it's a valid URL (starts with http)
  const hasValidLogo = project.logoUrl && project.logoUrl.startsWith('http');

  return (
    <div className={`flex items-center gap-1.5 ${className}`}>
      {hasValidLogo ? (
        <img 
          src={project.logoUrl!} 
          alt={project.name}
          className={`${sizeClass} rounded object-contain`}
        />
      ) : (
        <div 
          className={`${sizeClass} rounded flex items-center justify-center`}
          style={{ backgroundColor: color }}
        >
          <span className={`text-white font-bold ${textSize}`}>
            {project.name.charAt(0).toUpperCase()}
          </span>
        </div>
      )}
      {showName && (
        <span className="text-sm font-medium text-gray-700 truncate">
          {project.name}
        </span>
      )}
    </div>
  );
}

// Inline verze pro použití v badge/tag stylu
interface ProjectTagProps {
  projectId: string;
  className?: string;
}

export function ProjectTag({ projectId, className = '' }: ProjectTagProps) {
  const { getProjectById, getProjectColor } = useProject();
  const project = getProjectById(projectId);
  
  if (!project) return null;

  const color = getProjectColor(projectId);

  return (
    <span 
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}
      style={{ 
        backgroundColor: `${color}15`, 
        color: color 
      }}
    >
      {project.logoUrl && project.logoUrl.startsWith('http') ? (
        <img 
          src={project.logoUrl} 
          alt=""
          className="h-4 w-4 rounded object-contain"
        />
      ) : (
        <span 
          className="h-2 w-2 rounded-full" 
          style={{ backgroundColor: color }}
        />
      )}
      {project.name}
    </span>
  );
}

export default ProjectBadge;
